import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowRight, UserPlus, CalendarClock, BellRing } from 'lucide-react';
import { Button } from '../components/ui/button';
import { QuickClockModal } from '../components/QuickClock';
import { pricingAPI } from '../lib/api';
import { DEFAULT_PLANS } from '../components/landing/landingData';

import { LandingHeader }         from '../components/landing/LandingHeader';
import { HeroSection }           from '../components/landing/HeroSection';
import { MCPLandingSection }     from '../components/landing/MCPLandingSection';
import { ProblemSection }        from '../components/landing/ProblemSection';
import { BannerSection }         from '../components/landing/BannerSection';
import { TransformationSection } from '../components/landing/TransformationSection';
import { CoreFeaturesSection }   from '../components/landing/CoreFeaturesSection';
import { FeaturesSection }       from '../components/landing/FeaturesSection';
import { OutcomesSection }       from '../components/landing/OutcomesSection';
import { OtherFeaturesSection }  from '../components/landing/OtherFeaturesSection';
import { MobileAppSection }      from '../components/landing/MobileAppSection';
import { EvaAISection }          from '../components/landing/EvaAISection';
import { PricingSection }        from '../components/landing/PricingSection';
import { TrustReassuranceSection } from '../components/landing/TrustReassuranceSection';
import { CustomersSection }      from '../components/landing/CustomersSection';
import { TestimonialsSection }   from '../components/landing/TestimonialsSection';
import { FAQSection }            from '../components/landing/FAQSection';
import { CTASection }            from '../components/landing/CTASection';
import TryDemoSection            from '../components/landing/TryDemoSection';

const Footer = lazy(() => import('../components/Footer'));

export default function Landing() {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('yearly');
  const [quickClockOpen, setQuickClockOpen] = useState(false);
  const [plans, setPlans] = useState(DEFAULT_PLANS);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const heroRef = useRef(null);

  // Fetch live pricing
  useEffect(() => {
    const descOverrides = {
      single: 'Everything you need to schedule, track hours, and run payroll — in one place.',
      multi: 'Everything in Single Location, plus multi-location payroll and centralized reporting.',
    };
    pricingAPI.getPricing()
      .then(({ data }) => {
        const backendPlans = Object.values(data.plans).map((p) => ({
          key: p.key,
          name: p.name,
          monthlyPrice: p.monthly_price,
          yearlyMonthly: p.yearly_monthly_equivalent,
          yearlyTotal: p.yearly_price,
          badge: p.badge || null,
          desc: descOverrides[p.key] || p.description,
          features: p.features,
          cta: 'Start Free Trial',
        }));
        backendPlans.sort((a, b) => (a.key === 'single' ? -1 : 1));
        setPlans(backendPlans);
      })
      .catch(() => {});
  }, []);

  // SEO meta tags
  useEffect(() => {
    document.title = 'Workforce & Payroll Software | Scheduling, Time Tracking & Automatic Payroll | ReStaff';
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
    meta.content = 'ReStaff is a workforce and payroll automation platform. Schedule employees, track hours, auto-generate payslips from timesheets, and export payroll reports — all in one system.';

    let keywords = document.querySelector('meta[name="keywords"]');
    if (!keywords) { keywords = document.createElement('meta'); keywords.name = 'keywords'; document.head.appendChild(keywords); }
    keywords.content = 'payroll software, workforce management, employee scheduling, time tracking, payslip generation, labor cost optimization, staff management, payroll automation';
  }, []);

  // Scroll-to-top + sticky CTA (show after 25% scroll)
  useEffect(() => {
    const onScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;

      setShowScrollTop(scrollTop > 400);
      setShowStickyCTA(scrollPercent > 25);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 pb-24 md:pb-0">
      <LandingHeader onQuickClock={() => setQuickClockOpen(true)} />
      <QuickClockModal open={quickClockOpen} onOpenChange={setQuickClockOpen} />

      <div ref={heroRef}>
        <HeroSection onQuickClock={() => setQuickClockOpen(true)} />

      <MCPLandingSection />
      </div>
      <ProblemSection />
      <BannerSection />
      <TryDemoSection />
      <TransformationSection />
      <CoreFeaturesSection />
      <OutcomesSection />
      <EvaAISection />
      <FeaturesSection />
      
      {/* Blog Links Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-4">
            Learn How to Optimize Your Workforce
          </h2>
          <p className="text-center text-slate-500 mb-12 text-base md:text-lg">
            Practical guides to help you run a leaner, more efficient team.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                to: "/blog/how-to-reduce-labor-cost",
                icon: "💰",
                title: "How to Reduce Labor Cost",
                desc: "Proven strategies to lower your payroll expenses without cutting staff quality or morale.",
              },
              {
                to: "/blog/how-to-schedule-employees-efficiently",
                icon: "📅",
                title: "How to Schedule Employees Efficiently",
                desc: "Build smarter schedules that match demand, reduce gaps, and keep your team happy.",
              },
              {
                to: "/blog/how-to-track-employee-work-hours",
                icon: "⏱",
                title: "How to Track Employee Work Hours",
                desc: "Accurate time tracking methods that eliminate disputes and simplify payroll.",
              },
              {
                to: "/blog/how-to-reduce-overtime",
                icon: "📉",
                title: "How to Reduce Overtime",
                desc: "Identify overtime triggers early and implement guardrails to keep hours in check.",
              },
              {
                to: "/blog/manage-staff-multiple-locations",
                icon: "📍",
                title: "How to Manage Staff Across Multiple Locations",
                desc: "Centralize visibility and control so every site runs smoothly — even when you're not there.",
              },
              {
                to: "/blog/resend-smtp-staff-notifications",
                icon: "📧",
                title: "How to Automate Staff Email Notifications",
                desc: "Send shift updates, announcements, and payslips automatically using Resend.",
              },
            ].map((article) => (
              <Link
                key={article.to}
                to={article.to}
                className="group bg-white rounded-xl shadow-sm border border-slate-100 p-6 flex flex-col gap-3 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
              >
                <span className="text-2xl">{article.icon}</span>
                <h3 className="text-lg font-semibold text-slate-900 group-hover:text-orange-500 transition-colors duration-150">
                  {article.title}
                </h3>
                <p className="text-sm text-slate-500 leading-relaxed flex-1">
                  {article.desc}
                </p>
                <span className="text-sm font-medium text-orange-500 group-hover:text-orange-600 mt-1">
                  Read more →
                </span>
              </Link>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/blog" className="text-orange-500 hover:text-orange-600 font-medium transition-colors duration-150">
              View all articles →
            </Link>
          </div>
        </div>
      </section>
      
      <OtherFeaturesSection />

      {/* Team Onboarding Value Section */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10 md:mb-14">
            <span className="text-xs font-bold uppercase tracking-widest text-orange-500">Team Onboarding</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Manrope' }}>
              Get your team running in minutes
            </h2>
            <p className="mt-4 text-base md:text-lg text-slate-500 max-w-2xl mx-auto">
              Invite staff, assign shifts, and keep everyone informed without extra setup.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {[
              {
                icon: UserPlus,
                title: 'Invite staff instantly',
                desc: 'Send staff invitations by email so new team members can join quickly.',
                href: '/help/invite-staff',
              },
              {
                icon: CalendarClock,
                title: 'Assign shifts in seconds',
                desc: 'Create shifts fast and keep schedules organized as your team grows.',
              },
              {
                icon: BellRing,
                title: 'Keep everyone notified automatically',
                desc: 'Staff receive updates by email and in-app notifications so nothing gets missed.',
              },
            ].map(({ icon: Icon, title, desc, href }) => (
              <div
                key={title}
                onClick={href ? () => navigate(href) : undefined}
                className={`group relative bg-white rounded-2xl border border-slate-200 p-6 md:p-7 shadow-sm hover:shadow-lg hover:-translate-y-0.5 hover:border-orange-200 transition-all duration-200 ${href ? 'cursor-pointer' : ''}`}
                data-testid={`team-onboarding-card-${title.toLowerCase().split(' ')[0]}`}
              >
                <div className="w-11 h-11 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center mb-4 group-hover:bg-orange-100 transition-colors">
                  <Icon size={20} className="text-orange-500" strokeWidth={2.2} />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                  {title}
                </h3>
                <p className="text-sm text-slate-500 leading-relaxed">
                  {desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SMTP Email Notifications Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">

          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-4">
            Automated Staff Email Notifications
          </h2>

          <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto text-base md:text-lg">
            Send shift updates, announcements, and payslips automatically using your own Resend account — no extra email costs.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

            <div className="p-6 rounded-xl border border-slate-200 bg-slate-50 hover:shadow-md transition-shadow duration-200">
              <div className="text-3xl mb-3">📧</div>
              <h3 className="font-semibold text-lg mb-2 text-slate-900">Shift Notifications</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Automatically notify staff when shifts are assigned, updated, or cancelled — no manual follow-up needed.
              </p>
            </div>

            <div className="p-6 rounded-xl border border-slate-200 bg-slate-50 hover:shadow-md transition-shadow duration-200">
              <div className="text-3xl mb-3">📣</div>
              <h3 className="font-semibold text-lg mb-2 text-slate-900">Announcements</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Send important updates instantly to your entire team with company-wide or location-specific emails.
              </p>
            </div>

            <div className="p-6 rounded-xl border border-slate-200 bg-slate-50 hover:shadow-md transition-shadow duration-200">
              <div className="text-3xl mb-3">💰</div>
              <h3 className="font-semibold text-lg mb-2 text-slate-900">Payslips</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Automatically deliver payslips with professional branded emails — no printing or manual distribution.
              </p>
            </div>

          </div>

          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-lg px-4 py-2 mb-6">
              <span className="text-orange-600 font-semibold text-sm">3,000 emails/month included FREE</span>
              <span className="text-slate-500 text-xs">with Resend</span>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/register">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200">
                  Try Email Automation Free
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <Link to="/help/resend-smtp" className="text-slate-600 hover:text-orange-500 text-sm font-medium underline">
                Learn how it works →
              </Link>
            </div>
          </div>

        </div>
      </section>
      
      <MobileAppSection />
      <PricingSection plans={plans} billingCycle={billingCycle} setBillingCycle={setBillingCycle} />
      <TrustReassuranceSection />
      <CustomersSection />
      <TestimonialsSection />
      <FAQSection />
      <CTASection />

      <Suspense fallback={<div className="h-40" />}>
        <Footer />
      </Suspense>

      {/* Scroll-to-top — raised on mobile so it sits above the sticky CTA bar */}
      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          data-testid="scroll-to-top-btn"
          aria-label="Scroll to top"
          className="fixed bottom-24 right-4 md:bottom-6 md:right-6 z-50 w-11 h-11 rounded-full bg-orange-500 hover:bg-orange-600 text-white shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
        >
          <ArrowDown className="w-5 h-5 rotate-180" />
        </button>
      )}

      {/* Mobile sticky bottom CTA bar — shows after 25% scroll */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-40 md:hidden transition-all duration-300 ease-out ${
          showStickyCTA
            ? 'translate-y-0 opacity-100'
            : 'translate-y-full opacity-0 pointer-events-none'
        }`}
        data-testid="mobile-sticky-cta-wrapper"
        aria-hidden={!showStickyCTA}
      >
        <div className="bg-white/90 backdrop-blur-md border-t border-slate-200 px-4 py-3">
          <Link to="/register" tabIndex={showStickyCTA ? 0 : -1}>
            <Button
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-4 rounded-xl shadow-lg flex items-center justify-center gap-2"
              data-testid="mobile-sticky-cta-btn"
            >
              Start Free Trial
              <ArrowRight size={16} />
            </Button>
          </Link>
        </div>
      </div>

      {/* Desktop sticky Start Free Trial pill CTA (mobile hidden) */}
      <div
        className={`hidden md:block fixed bottom-6 left-1/2 z-40 transition-all duration-200 ${
          showStickyCTA
            ? '-translate-x-1/2 translate-y-0 opacity-100'
            : '-translate-x-1/2 translate-y-4 opacity-0 pointer-events-none'
        }`}
        data-testid="sticky-cta-wrapper"
        aria-hidden={!showStickyCTA}
      >
        <Link to="/register" tabIndex={showStickyCTA ? 0 : -1}>
          <Button
            className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-6 py-2.5 shadow-lg shadow-orange-500/25 hover:shadow-xl hover:shadow-orange-500/35 hover:scale-105 text-sm font-semibold whitespace-nowrap transition-all duration-200"
            data-testid="sticky-cta-btn"
          >
            Start Free Trial
            <ArrowRight size={15} className="ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  );
}
